class ImperiaHomeHeaderSentinel extends HTMLElement {
  observer;
  header;

  connectedCallback() {
    this.header = document.querySelector('#header-component');
    if (!this.header) return;

    this.observer = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const hasScrolledPastHeroStart = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.header.classList.toggle('imperia-home-header--scrolled', hasScrolledPastHeroStart);
    });

    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.header?.classList.remove('imperia-home-header--scrolled');
  }
}

if (!customElements.get('imperia-home-header-sentinel')) {
  customElements.define('imperia-home-header-sentinel', ImperiaHomeHeaderSentinel);
}

class ImperiaProductReel extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.viewport = this.querySelector('[data-reel-viewport]');
    this.track = this.querySelector('[data-reel-track]');
    this.primarySet = this.querySelector('[data-reel-set]');
    if (!this.viewport || !this.track || !this.primarySet) return;

    this.initialized = true;
    this.speed = Number.parseFloat(this.dataset.speed) || 25;
    this.offset = 0;
    this.setWidth = 0;
    this.lastFrame = 0;
    this.animationFrame = 0;
    this.isHovered = false;
    this.isFocused = false;
    this.isDragging = false;
    this.isInViewport = false;
    this.pointerId = null;
    this.dragStartX = 0;
    this.dragStartOffset = 0;
    this.hasDragged = false;
    this.suppressClick = false;
    this.initializationFrame = 0;

    this.desktopMedia = window.matchMedia('(min-width: 750px)');
    this.reducedMotionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.viewport.addEventListener('mouseenter', this.handleMouseEnter);
    this.viewport.addEventListener('mouseleave', this.handleMouseLeave);
    this.viewport.addEventListener('focusin', this.handleFocusIn);
    this.viewport.addEventListener('focusout', this.handleFocusOut);
    this.viewport.addEventListener('pointerdown', this.handlePointerDown);
    this.viewport.addEventListener('pointermove', this.handlePointerMove);
    this.viewport.addEventListener('pointerup', this.handlePointerEnd);
    this.viewport.addEventListener('pointercancel', this.handlePointerEnd);
    this.viewport.addEventListener('click', this.handleClick, true);
    this.viewport.addEventListener('dragstart', this.preventNativeDrag);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.desktopMedia.addEventListener('change', this.handleModeChange);
    this.reducedMotionMedia.addEventListener('change', this.handleModeChange);

    this.resizeObserver = new ResizeObserver(this.measure);
    this.resizeObserver.observe(this.primarySet);
    this.resizeObserver.observe(this.viewport);

    this.intersectionObserver = new IntersectionObserver(this.handleIntersection, {
      rootMargin: '100px 0px',
      threshold: 0.01,
    });
    this.intersectionObserver.observe(this);

    this.measure();
    this.initializationFrame = requestAnimationFrame(this.initializePlayback);
  }

  disconnectedCallback() {
    if (!this.initialized) return;

    this.stopAnimation();
    if (this.initializationFrame) cancelAnimationFrame(this.initializationFrame);
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    this.viewport?.removeEventListener('mouseenter', this.handleMouseEnter);
    this.viewport?.removeEventListener('mouseleave', this.handleMouseLeave);
    this.viewport?.removeEventListener('focusin', this.handleFocusIn);
    this.viewport?.removeEventListener('focusout', this.handleFocusOut);
    this.viewport?.removeEventListener('pointerdown', this.handlePointerDown);
    this.viewport?.removeEventListener('pointermove', this.handlePointerMove);
    this.viewport?.removeEventListener('pointerup', this.handlePointerEnd);
    this.viewport?.removeEventListener('pointercancel', this.handlePointerEnd);
    this.viewport?.removeEventListener('click', this.handleClick, true);
    this.viewport?.removeEventListener('dragstart', this.preventNativeDrag);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.desktopMedia?.removeEventListener('change', this.handleModeChange);
    this.reducedMotionMedia?.removeEventListener('change', this.handleModeChange);
    this.initialized = false;
  }

  canAnimate() {
    return (
      this.desktopMedia.matches &&
      !this.reducedMotionMedia.matches &&
      document.visibilityState === 'visible' &&
      this.isInViewport &&
      !this.isHovered &&
      !this.isFocused &&
      !this.isDragging &&
      this.setWidth > this.viewport.clientWidth
    );
  }

  measure = () => {
    const renderedWidth = this.primarySet?.getBoundingClientRect().width || 0;
    const contentWidth = this.primarySet?.scrollWidth || 0;
    this.setWidth = Math.max(renderedWidth, contentWidth);
    this.normalizeOffset();
    this.render();
    this.updatePlayback();
  };

  initializePlayback = () => {
    this.initializationFrame = 0;
    if (!this.initialized) return;

    this.measure();
    this.updateViewportState();
  };

  updateViewportState() {
    const bounds = this.getBoundingClientRect();
    this.isInViewport = bounds.bottom > -100 && bounds.top < window.innerHeight + 100;
    this.updatePlayback();
  }

  normalizeOffset() {
    if (!this.setWidth) return;
    this.offset %= this.setWidth;
    if (this.offset < 0) this.offset += this.setWidth;
  }

  render() {
    if (!this.track) return;
    this.track.style.transform = `translate3d(${-this.offset}px, 0, 0)`;
  }

  updatePlayback() {
    if (this.canAnimate()) {
      this.startAnimation();
    } else {
      this.stopAnimation();
    }
  }

  startAnimation() {
    if (this.animationFrame) return;
    this.classList.add('is-playing');
    this.lastFrame = performance.now();
    this.animationFrame = requestAnimationFrame(this.animate);
  }

  stopAnimation() {
    this.classList.remove('is-playing');
    if (!this.animationFrame) return;
    cancelAnimationFrame(this.animationFrame);
    this.animationFrame = 0;
  }

  animate = (time) => {
    if (!this.canAnimate()) {
      this.classList.remove('is-playing');
      this.animationFrame = 0;
      return;
    }

    const elapsed = Math.min(time - this.lastFrame, 64);
    this.lastFrame = time;
    this.offset += (this.speed * elapsed) / 1000;
    this.normalizeOffset();
    this.render();
    this.animationFrame = requestAnimationFrame(this.animate);
  };

  handleIntersection = ([entry]) => {
    this.isInViewport = Boolean(entry?.isIntersecting);
    this.updatePlayback();
  };

  handleMouseEnter = () => {
    this.isHovered = true;
    this.updatePlayback();
  };

  handleMouseLeave = () => {
    this.isHovered = false;
    this.updatePlayback();
  };

  handleFocusIn = (event) => {
    this.isFocused = true;
    const card = event.target.closest?.('.imperia-reel-card');
    if (card && this.primarySet.contains(card)) this.revealCard(card);
    this.updatePlayback();
  };

  handleFocusOut = () => {
    requestAnimationFrame(() => {
      if (!this.initialized) return;
      this.isFocused = this.contains(document.activeElement);
      this.updatePlayback();
    });
  };

  revealCard(card) {
    if (!this.desktopMedia.matches || this.reducedMotionMedia.matches) return;

    const viewportRect = this.viewport.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const safeInset = 24;

    if (cardRect.left < viewportRect.left + safeInset) {
      this.offset -= viewportRect.left + safeInset - cardRect.left;
    } else if (cardRect.right > viewportRect.right - safeInset) {
      this.offset += cardRect.right - (viewportRect.right - safeInset);
    }

    const maximumOffset = Math.max(0, this.setWidth - this.viewport.clientWidth);
    this.offset = Math.min(Math.max(this.offset, 0), maximumOffset);
    this.render();
  }

  handlePointerDown = (event) => {
    if (!this.desktopMedia.matches || this.reducedMotionMedia.matches || event.button !== 0) return;

    this.isDragging = true;
    this.pointerId = event.pointerId;
    this.dragStartX = event.clientX;
    this.dragStartOffset = this.offset;
    this.hasDragged = false;
    this.viewport.classList.add('is-dragging');
    this.viewport.setPointerCapture(event.pointerId);
    this.updatePlayback();
  };

  handlePointerMove = (event) => {
    if (!this.isDragging || event.pointerId !== this.pointerId) return;

    const distance = event.clientX - this.dragStartX;
    if (Math.abs(distance) > 4) this.hasDragged = true;
    if (!this.hasDragged) return;

    event.preventDefault();
    this.offset = this.dragStartOffset - distance;
    this.normalizeOffset();
    this.render();
  };

  handlePointerEnd = (event) => {
    if (!this.isDragging || event.pointerId !== this.pointerId) return;

    if (this.viewport.hasPointerCapture(event.pointerId)) {
      this.viewport.releasePointerCapture(event.pointerId);
    }

    this.isDragging = false;
    this.pointerId = null;
    this.viewport.classList.remove('is-dragging');

    if (this.hasDragged) {
      this.suppressClick = true;
      setTimeout(() => {
        this.suppressClick = false;
      }, 0);
    }

    this.updatePlayback();
  };

  handleClick = (event) => {
    if (!this.suppressClick) return;
    event.preventDefault();
    event.stopPropagation();
  };

  preventNativeDrag = (event) => {
    event.preventDefault();
  };

  handleVisibilityChange = () => {
    this.updatePlayback();
  };

  handleModeChange = () => {
    this.stopAnimation();
    this.offset = 0;
    this.viewport.scrollLeft = 0;
    this.render();
    this.measure();
  };
}

if (!customElements.get('imperia-product-reel')) {
  customElements.define('imperia-product-reel', ImperiaProductReel);
}
